import { get } from 'lodash';
import { useSelector } from 'react-redux';
import { userSelector } from 'app/selectors';
import StyledForm from './StyledForm';
import { useForm } from 'react-hook-form';
import CustomInput from 'components/fields/input2';
import Button from 'components/Button';
import MySelect from 'components/MySelect';
import { useEffect, useMemo, useState } from 'react';
import { handleError, request } from 'app/api';
import { CircularProgress, IconButton, Skeleton } from '@mui/material';
import { useContext } from 'react';
import TableRow from 'components/table/CustomTableRow';
import TableCol from 'components/table/CustomTableCol';
import { LanguageContext } from 'components/language';
import { toast } from 'react-toastify';
import { useApi, useDialog, useMessage } from 'app/hooks';
import StyledModal from './StyledUpdateModal';
import { ReactComponent as TrashIcon } from 'assets/img/Trash_light.svg';
import { ReactComponent as EditIcon } from 'assets/img/Edit_light.svg';
import { chain } from 'lodash';

const initialValues = {
    rateId: null,
    bookingComRoomId: '',
    bookingComRoomName: '',
};

const RoomsIntegration = () => {
    const user = useSelector(userSelector);
    const [{ roomsterCategories, bookingComCategories }, setData] = useState({});
    const [loading, setLoading] = useState(false);
    const { t } = useContext(LanguageContext);
    const activeHotelId = get(user, 'activeHotelId');
    const { data: categoriesData = {} } = useApi(`category/get-all/${localStorage.locale}?.pageNumber=${0}&pageSize=${10000}`);
    const categories = get(categoriesData, 'categories', []);
    const [uploading, setUploading] = useState(false);
    const [isOpen, handleShow, hide] = useDialog();
    const [activeBookingComRoom, setActiveBookingComRoom] = useState(null);
    const { handleSubmit, control, reset } = useForm({ defaultValues: initialValues });
    const { setMessage } = useMessage();

    const connectedRooms = useMemo(() => {
        return chain(roomsterCategories)
            .filter((item) => item?.bookingComUnitId)
            .keyBy('bookingComUnitId')
            .value();
    }, [roomsterCategories]);

    const notConnectedRooms = useMemo(() => {
        return roomsterCategories?.filter((item) => !item?.bookingComUnitId);
    }, [roomsterCategories]);

    const fetchData = () => {
        setLoading(true);
        request({
            url: `/booking-com/rooms?hotelId=${activeHotelId}`,
            success: ({ data }) => {
                if (data) setData(data);
                setLoading(false);
            },
            fail: () => {
                setLoading(false);
            },
        });
    };

    const handleDelete = (roomId) => {
        setMessage({
            text: 'Are you sure you want to permanently disconnect the room?',
            confirm: true,

            yes: () => {
                request({
                    method: 'put',
                    url: `booking-com/room/disconnect?roomId=${roomId}`,
                    success: () => {
                        setMessage({});
                        fetchData();
                    },
                    fail: (e) => handleError(e),
                });
            },
            no: () => {
                setMessage({});
            },
        });
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeHotelId]);

    const onSubmit = (data) => {
        setUploading(true);
        request({
            url: `/booking-com/room`,
            method: 'post',
            params: data,
            success: () => {
                reset(initialValues);
                toast.success(t('ROOM_CONNECTED_SUCCESSFULLY'));
                fetchData();
            },
            fail: handleError,
            finish: () => {
                setUploading(false);
            },
        });
    };

    return (
        <div className="main-table-container">
            <StyledForm onSubmit={handleSubmit(onSubmit)}>
                <MySelect
                    nullable={false}
                    control={control}
                    name="roomId"
                    options={categories}
                    isSearchable
                    isClearable
                    sx={{ width: '200px' }}
                    placeholder={t('ROOM')}
                />
                <CustomInput type="number" control={control} name="bookingComRoomId" placeholder={t('BOOKING_COM_ROOM_ID')} />
                <CustomInput control={control} name="bookingComRoomName" placeholder={t('BOOKING_COM_ROOM_NAME')} />
                <Button type="submit" disabled={uploading}>
                    {uploading ? <CircularProgress size={20} /> : t('SAVE')}
                </Button>
            </StyledForm>

            <div className="category-body">
                <TableRow head>
                    <TableCol width={30}>#</TableCol>
                    <TableCol>{t('NAME')}</TableCol>
                    <TableCol>{t('BOOKING_COM_ROOM_ID')}</TableCol>
                    <TableCol>{t('BOOKING_COM_ROOM_NAME')}</TableCol>
                    <TableCol width={100}>{t('ACTIONS')}</TableCol>
                </TableRow>

                <div className="body">
                    {loading
                        ? Array(4)
                              .fill(0)
                              .map((_, i) => (
                                  <TableRow key={i}>
                                      <TableCol>
                                          <Skeleton width={'100%'} animation="wave" height={35} />
                                      </TableCol>
                                  </TableRow>
                              ))
                        : bookingComCategories?.map((item, index) => (
                              <TableRow key={item.id}>
                                  <TableCol width={30}>{index + 1}</TableCol>
                                  <TableCol>{get(connectedRooms, [item.unit_id, 'name'], '-')}</TableCol>
                                  <TableCol>{item.unit_id || '-'}</TableCol>
                                  <TableCol>{item.partner_reference_name || '-'}</TableCol>
                                  <TableCol width={100}>
                                      <IconButton
                                          onClick={() => {
                                              handleShow();
                                              setActiveBookingComRoom(item);
                                          }}
                                      >
                                          <EditIcon />
                                      </IconButton>
                                      <IconButton
                                          disabled={!get(connectedRooms, [item.unit_id, 'id'])}
                                          onClick={() => handleDelete(get(connectedRooms, [item.unit_id, 'id']))}
                                      >
                                          <TrashIcon />
                                      </IconButton>
                                  </TableCol>
                              </TableRow>
                          ))}
                </div>
            </div>

            <SelectRoomModal isOpen={isOpen} onClose={hide} onSuccess={fetchData} rooms={notConnectedRooms} bookingComRoom={activeBookingComRoom} />
        </div>
    );
};

const SelectRoomModal = ({ isOpen, onClose, onSuccess, rooms, bookingComRoom }) => {
    const { t } = useContext(LanguageContext);
    const [updating, setUpdating] = useState(false);

    const { handleSubmit, control, reset } = useForm({ defaultValues: { roomId: null } });

    const onSubmit = (data) => {
        setUpdating(true);
        request({
            url: `/booking-com/room`,
            method: 'put',
            params: { ...data, bookingComRoomId: bookingComRoom.unit_id, bookingComRoomName: bookingComRoom.partner_reference_name },
            success: () => {
                toast.success(t('ROOM_UPDATED_SUCCESSFULLY'));
                reset({ roomId: null });
                onClose();
                onSuccess();
            },
            fail: handleError,
            finish: () => {
                setUpdating(false);
            },
        });
    };

    return (
        <StyledModal
            active={isOpen}
            onClose={() => {
                reset({ rateId: null });
                onClose();
            }}
        >
            <div className="modal-content">
                <h4>{t('SELECT_A_ROOM')}</h4>

                <form className="modal-content-body" onSubmit={handleSubmit(onSubmit)}>
                    <MySelect nullable={false} control={control} name="roomId" options={rooms} sx={{ width: '100%' }} placeholder={t('ROOM')} />

                    <div className="modal-content-footer">
                        <Button loading={updating} color="primary" type="submit">
                            {t('SAVE')}
                        </Button>
                    </div>
                </form>
            </div>
        </StyledModal>
    );
};
export default RoomsIntegration;
